// #341 — tiny widget performance instrumentation (measurement only).
//
// A thin wrapper over the browser User Timing API (performance.mark /
// performance.measure). It records named marks + spans that show up in the
// browser's Performance timeline (DevTools → Performance, and the
// PerformanceObserver API) so widget timings can be inspected with real
// numbers instead of inference.
//
// DESIGN CONSTRAINTS:
// - Lean: the widget bundle has an 80KB gzipped cap. This module is a handful
//   of guarded one-liners and adds well under the issue's <500 byte budget.
// - Behavior-preserving: every call is wrapped so a missing / throwing
//   `performance` API can never affect widget behavior. Instrumentation that
//   breaks the widget would be worse than no instrumentation.
// - Reusable: `mark` and `measure` are exported so the other perf slices can
//   add their own marks (capture, compress, reserve, upload stages) without
//   re-implementing the guards. Names are prefixed `oncatch:` so they're
//   trivially filterable from host-page + browser timeline entries.
//
// This slice intentionally instruments only bootstrap + the config fetch. It
// does NOT wire durations into any network payload (that would be a behavior
// change); later slices consume these marks.

const PREFIX = 'oncatch:';

/** Named marks placed by THIS slice. Other slices add their own string names. */
export const WidgetMark = {
  bootstrapStart: 'bootstrap:start',
  bootstrapEnd: 'bootstrap:end',
  configFetchStart: 'config-fetch:start',
  configFetchEnd: 'config-fetch:end',
  // #533 — the submit flow. `submit:*` spans the whole click-to-answer path;
  // `uploads-settle:*` isolates the new wait-for-in-flight-uploads gate so a
  // slow submission can be attributed to the upload pipeline rather than to
  // the server round trip.
  submitStart: 'submit:start',
  submitEnd: 'submit:end',
  uploadsSettleStart: 'uploads-settle:start',
  uploadsSettleEnd: 'uploads-settle:end',
} as const;

/**
 * #579 — per-attachment pipeline stages. These are the three phases an
 * attachment passes through before it can be submitted.
 */
export type AttachmentStage = 'compress' | 'reserve' | 'upload';

function canMark(): boolean {
  return typeof performance !== 'undefined' && typeof performance.mark === 'function';
}

/**
 * Place a named mark on the performance timeline. No-op (swallowed) when the
 * API is unavailable or throws.
 */
export function mark(name: string): void {
  if (!canMark()) return;
  try {
    performance.mark(PREFIX + name);
  } catch {
    // never let instrumentation affect widget behavior
  }
}

/**
 * Record a measure spanning two previously-placed marks. Best-effort: if either
 * mark is missing or the API throws, the measure is silently skipped. Returns
 * nothing — consumers read durations from the timeline / a PerformanceObserver,
 * not from a return value (keeps callers from branching on instrumentation).
 */
export function measure(name: string, startMark: string, endMark: string): void {
  if (typeof performance === 'undefined' || typeof performance.measure !== 'function') return;
  try {
    performance.measure(PREFIX + name, PREFIX + startMark, PREFIX + endMark);
  } catch {
    // start/end mark missing, or API threw — skip silently
  }
}

// ---------------------------------------------------------------------------
// #579 — attachment pipeline stages
// ---------------------------------------------------------------------------
//
// WHY THE SLOT ID IS IN THE MARK NAME, and why removing it silently corrupts
// the data rather than merely degrading it:
//
// Attachment slots run CONCURRENTLY — a reporter can add a second screenshot
// while the first is still uploading. `performance.measure(name, start, end)`
// resolves each mark name to its MOST RECENT entry. So if two slots shared the
// mark name `upload:start`, slot B's start would overwrite slot A's, and slot
// A's measure would span from B's start to A's end: a fabricated duration that
// looks perfectly plausible in the timeline. Timing data that is quietly wrong
// is worse than none, because it gets acted on.
//
// The MEASURE name stays stable (`upload`, not `upload:<id>`) so every slot
// aggregates under one entry type — which is what makes the numbers rankable.

/** Place a start/end mark for one stage of one attachment slot. */
export function markStage(stage: AttachmentStage, slotId: string, edge: 'start' | 'end'): void {
  mark(`${stage}:${edge}:${slotId}`);
}

/**
 * Close out a stage for one slot. Safe to call even if the stage failed — a
 * failed upload's duration is exactly what you want when the question is "what
 * is slow", so callers should invoke this from a `finally`.
 */
export function measureStage(stage: AttachmentStage, slotId: string): void {
  measure(stage, `${stage}:start:${slotId}`, `${stage}:end:${slotId}`);
}
